import { defineComponent, PropType } from "vue";

import { COMPONENT_NAME, SVG } from "./attributes";

import { SvgAttribute } from "@/shared/constants/constants";
import { WalletListResponse } from "@/shared/repository/modules/wallet/repo";
import { WalletModalFactory } from "@/shared/repository/modules/wallet/factory";
import { AdminApplicationType } from "@/shared/repository/modules/admin/application/enums";
import { AdminCurrencyType } from "@/shared/repository/modules/admin/currency/enums";
import {
  formatFiatCurrency,
  formatCryptoCurrency,
} from "@/shared/utils/amount-helpers";
import { ApplicationStoreRequestFactory } from "@/shared/repository/modules/application/factory";
import {
  HistoryTab,
  RoutesName,
  HistoryFiatCryptoTab,
} from "@/shared/constants/enums";
import { HistoryFilterRequestFactory } from "@/shared/factory/history";
import { animateValue } from "@/shared/utils/animation-helpers";

const TINT_COLORS: Record<string, string> = {
  USD: "57, 127, 223",
  USDT: "50, 189, 136",
  BTC: "255, 163, 49",
  KZT: "41, 180, 200",
  RUB: "103, 104, 231",
  EUR: "80, 123, 220",
};

export default defineComponent({
  name: COMPONENT_NAME,
  props: {
    row: {
      type: <PropType<WalletListResponse>>Object,
      required: true,
    },
  },
  data() {
    return {
      isActive: false,
      animatedAmount: 0,
      rafId: <number | null>null,
      walletRepo: this.$projectServices.walletRepo,
      applicationRepo: this.$projectServices.applicationRepo,
      historyRepo: this.$projectServices.historyRepo,
      userRepo: this.$projectServices.userRepo,
    };
  },
  computed: {
    displayedIcons(): SvgAttribute {
      return SVG;
    },

    displayedAmount(): string {
      const { type } = this.row;
      const amount = this.animatedAmount;
      const result =
        type == AdminCurrencyType.fiat
          ? formatFiatCurrency(amount)
          : formatCryptoCurrency(amount);
      if (!amount) return "0,00";
      return type === AdminCurrencyType.fiat
        ? Number(amount).toLocaleString("ru-RU", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : result;
    },
    currencyCode(): string {
      return (this.row.currency?.name || this.row.name || "").toUpperCase();
    },
    currencySymbol(): string {
      const symbols: Record<string, string> = {
        USD: "$",
        USDT: "₮",
        BTC: "₿",
        KZT: "₸",
        RUB: "₽",
        EUR: "€",
      };
      return symbols[this.currencyCode] || this.currencyCode.slice(0, 3);
    },
    currencyTitle(): string {
      const names: Record<string, string> = {
        USD: "US Dollar",
        USDT: "Tether",
        BTC: "Bitcoin",
        KZT: "Казахстанский тенге",
        RUB: "Российский рубль",
        EUR: "Евро",
      };
      return names[this.currencyCode] || this.currencyCode;
    },
    usdEquivalent(): string {
      // Only show an equivalent when its value is known without a market quote.
      if (this.currencyCode !== "USD" && Number(this.row.amount) !== 0)
        return "";
      return (
        "≈ $" +
        Number(this.row.amount).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      );
    },
    notVerified(): boolean {
      return !this.userRepo.isVerified;
    },
    tintColor(): string {
      const rgb = TINT_COLORS[this.currencyCode] || TINT_COLORS.USD;
      return `rgba(${rgb}, 0.18)`;
    },
  },
  methods: {
    handleDeposit(): void {
      if (this.row.type == AdminCurrencyType.fiat) {
        this.walletRepo.UPDATE_MODAL(
          WalletModalFactory({
            type: AdminApplicationType.deposit,
            currencyId: this.row.currency_id,
            amount: this.row.amount,
            isDisabled: true,
          })
        );
      } else {
        this.walletRepo.UPDATE_MODAL(
          WalletModalFactory({
            type: AdminApplicationType.criptoDepositAddress,
            currencyId: this.row.currency_id,
          })
        );
      }
    },

    handleWithdrawal(): void {
      if (this.row.type == AdminCurrencyType.fiat) {
        this.walletRepo.UPDATE_MODAL(
          WalletModalFactory({
            type: AdminApplicationType.withdrawal,
            currencyId: this.row.currency_id,
            amount: this.row.amount,
            isDisabled: true,
          })
        );
      } else {
        this.walletRepo.UPDATE_MODAL(
          WalletModalFactory({
            type: AdminApplicationType.walletWithdrawal,
            currencyId: this.row.currency_id,
          })
        );
      }
    },

    handleApplication(): void {
      const { type, currency_id, amount } = this.row;

      if (amount) {
        this.applicationRepo.UPDATE_APPLICATION(
          ApplicationStoreRequestFactory({
            currency_id_from: currency_id,
            currency_type_from: type,
          })
        );
      } else {
        this.applicationRepo.UPDATE_APPLICATION(
          ApplicationStoreRequestFactory({
            currency_id_to: currency_id,
            currency_type_to: type,
          })
        );
      }

      this.$router.push(RoutesName.application);
    },

    handleHistory(): void {
      if (this.row.type == AdminCurrencyType.fiat) {
        this.historyRepo.UPDATE_TAB(HistoryTab.depositWithdrawal);

        this.historyRepo.UPDATE_FILTER(
          HistoryFilterRequestFactory({
            currency_id: `[${this.row.currency_id}]`,
          })
        );

        this.$router.push(RoutesName.history);
      } else {
        this.historyRepo.UPDATE_FIAT_CRYPTO_TAB(HistoryFiatCryptoTab.crypto);
        this.historyRepo.UPDATE_FILTER(
          HistoryFilterRequestFactory({
            coin: this.row.name,
          })
        );
        this.$router.push(RoutesName.history);
      }
    },

    animateAmount(from: number, to: number): void {
      if (this.rafId !== null) cancelAnimationFrame(this.rafId);

      this.rafId = animateValue(from, to, (value) => {
        this.animatedAmount = value;
      });
    },
  },
  watch: {
    "row.amount"(newAmount: number, oldAmount: number): void {
      this.animateAmount(Number(oldAmount) || 0, Number(newAmount) || 0);
    },
  },
  mounted(): void {
    this.animateAmount(0, Number(this.row.amount) || 0);
  },
  beforeUnmount(): void {
    if (this.rafId !== null) cancelAnimationFrame(this.rafId);
  },
});
